import { ClickAwayListener } from '@byecode/ui'
import type { Field, FieldADTValue } from '@lighthouse/core'
import type { FetchPexelsParams, PexelsResponse, UseUploadFileSParameter } from '@lighthouse/shared'
import {
    FieldValueEditor,
    FieldValueMobileEditor,
    getIsAppointField,
    NO_EDIT_SYSTEM_FIELD,
    notEditableFieldTypes,
    selectRecordFromPool,
    useApplicationContext,
    useAtomData,
    useCellEditorPosition,
    WYSIWYGFieldTypes
} from '@lighthouse/shared'
import { isSafaris } from '@lighthouse/tools'
import { Modal } from '@mantine/core'
import type { UploadyProps } from '@rpldy/uploady'
import React, { memo, useCallback, useDeferredValue, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'

interface TablePopoverProps {
    field: Field
    scrollRef: React.RefObject<HTMLDivElement>
    popperAnchorElement?: HTMLDivElement
    recordId: string
    width?: number
    required?: boolean
    uploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    richTextUploadOptions: UploadyProps
    videoUploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onCellUpdate?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onClose?: () => void
    onFetchPexelsList: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

const SCxDiv = styled.div``

const SCxWindowShapeEditorPopover = styled.div`
    position: fixed;
    min-width: 100px;
    min-height: 40px;
    overflow: hidden;
    display: inline-table;
    box-shadow: var(--box-shadow);
    background-color: var(--color-white);
    z-index: 200;
`
const SCxWindowShapeEditorModal = styled(Modal)``

const SCxEditorContent = styled.div`
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`

const SCxFieldEditorDrawerEditorContent = styled.div`
    flex: 1;
    height: 100%;
`

const SCxWindowShapeEditorModalOverlay = styled.div`
    outline: none;
    overflow: hidden;
`

export const TablePopover: React.FC<TablePopoverProps> = memo(
    ({
        field,
        scrollRef,
        popperAnchorElement,
        width = 160,
        required,
        recordId,
        uploadOptions,
        richTextUploadOptions,
        videoUploadOptions,
        onCellChange,
        onCellUpdate,
        onClose,
        onFetchPexelsList
    }) => {
        const { dataSource, recordPoolAtom } = useViewBlockContext()
        const [open, setOpen] = useState(false)
        const { pageTarget, isMobile } = useApplicationContext()
        const { id: dsId } = dataSource
        const { id: fieldId } = field
        // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
        const selector = useMemo(() => selectRecordFromPool(dsId, recordId), [dsId, recordId])
        const currentRecord = useDeferredValue(useAtomData(recordPoolAtom, selector))

        const fieldValue = useMemo(() => currentRecord?.content?.[fieldId]?.value, [currentRecord?.content, fieldId])

        const [currentData, setCurrentData] = useState(fieldValue)
        const isNoEditSystemField = getIsAppointField(fieldId, NO_EDIT_SYSTEM_FIELD)
        const isNoneShape = notEditableFieldTypes.has(field?.type || '')
        const isInput = WYSIWYGFieldTypes.has(field?.type || '')
        const isPopperShape = !isNoneShape && !['notes'].includes(field?.type || '') && !isNoEditSystemField
        const isModalShape = useMemo(() => ['notes'].includes(field?.type || ''), [field?.type])

        const { popoverRef, handleClearAnchorPosition, position } = useCellEditorPosition({
            scrollElement: scrollRef.current,
            anchorElement: popperAnchorElement,
            open,
            onOpenChange: setOpen,
            offset: 2
        })

        const handleClickOutside = useCallback(
            (isActive?: boolean) => {
                setTimeout(() => {
                    onClose?.()
                }, 0)
            },
            [onClose]
        )

        const handleCellChange = useCallback(
            async (recordId: string, fieldValue: FieldADTValue) => {
                setCurrentData(fieldValue.value)
                const isUpdate = await onCellChange?.(recordId, fieldValue)
                return !!isUpdate
            },
            [onCellChange]
        )

        const handleClose = useCallback(() => {
            setOpen(false)
            onClose?.()
            handleClearAnchorPosition()
        }, [handleClearAnchorPosition, onClose])

        const mouseEvent = useMemo(() => {
            if (isSafaris()) {
                return 'onMouseUp'
            }
            return 'onMouseDown'
        }, [])

        return useMemo(() => {
            if (isMobile) {
                if (isModalShape) {
                    return (
                        <Modal
                            opened={open}
                            target={pageTarget}
                            styles={{
                                root: {
                                    position: 'absolute'
                                },
                                modal: {
                                    height: '100%'
                                },
                                inner: {
                                    padding: 0
                                },
                                close: {
                                    display: 'none'
                                },
                                overlay: {
                                    borderRadius: pageTarget ? '55px!important' : 0
                                },
                                header: {
                                    marginBottom: 0
                                },
                                body: {
                                    position: 'relative',
                                    height: '100%'
                                }
                            }}
                            padding={0}
                            onClose={handleClickOutside}
                        >
                            <SCxEditorContent>
                                {field && (
                                    <SCxFieldEditorDrawerEditorContent>
                                        <FieldValueEditor
                                            uploadOptions={uploadOptions}
                                            videoUploadOptions={videoUploadOptions}
                                            richTextUploadOptions={richTextUploadOptions}
                                            dataSourceInfo={dataSource}
                                            data={currentData}
                                            currentRecord={currentRecord}
                                            scrollElement={scrollRef.current}
                                            field={field}
                                            recordId={recordId}
                                            autoFocus
                                            onCellChange={handleCellChange}
                                            onCellUpdate={onCellUpdate}
                                            onClose={handleClickOutside}
                                            onFetchPexelsList={onFetchPexelsList}
                                        />
                                    </SCxFieldEditorDrawerEditorContent>
                                )}
                            </SCxEditorContent>
                        </Modal>
                    )
                }

                if (!isInput) {
                    return (
                        <FieldValueMobileEditor
                            opened={open}
                            onClose={handleClickOutside}
                            field={field}
                            recordId={recordId}
                            value={currentData}
                            target={pageTarget}
                            onChange={handleCellChange}
                            uploadyOptions={uploadOptions}
                        />
                    )
                }
            }

            if (isModalShape) {
                return (
                    <SCxWindowShapeEditorModal
                        centered
                        closeOnClickOutside={false}
                        padding={0}
                        radius={12}
                        size={720}
                        // zIndex={9999}
                        overlayColor="var(--color-black)"
                        overlayOpacity={0.75}
                        withCloseButton={false}
                        opened={open}
                        // closeOnEscape={false}
                        onClose={handleClickOutside}
                    >
                        <SCxWindowShapeEditorModalOverlay style={{ height: 'calc(100vh - 344px)' }}>
                            {field && (
                                <FieldValueEditor
                                    editorHost="advancedTable"
                                    uploadOptions={uploadOptions}
                                    videoUploadOptions={videoUploadOptions}
                                    richTextUploadOptions={richTextUploadOptions}
                                    dataSourceInfo={dataSource}
                                    data={currentData}
                                    currentRecord={currentRecord}
                                    scrollElement={scrollRef.current}
                                    field={field}
                                    recordId={recordId}
                                    onCellChange={handleCellChange}
                                    onCellUpdate={onCellUpdate}
                                    onClose={handleClickOutside}
                                    onFetchPexelsList={onFetchPexelsList}
                                />
                            )}
                        </SCxWindowShapeEditorModalOverlay>
                    </SCxWindowShapeEditorModal>
                )
            }

            if (isPopperShape) {
                const extraStyles: React.CSSProperties = isInput
                    ? {
                          border: '2px solid var(--color-app-main)',
                          ...position
                      }
                    : {
                          border: '1px solid var(--color-gray-200)',
                          ...position
                      }
                return (
                    <SCxDiv>
                        {open &&
                            popperAnchorElement &&
                            createPortal(
                                <ClickAwayListener data-ignore-click-away mouseEvent={mouseEvent} onClickAway={handleClose}>
                                    <SCxWindowShapeEditorPopover
                                        onClick={e => e.stopPropagation()}
                                        style={{ width: width - 5, ...extraStyles }}
                                        data-ignore-click-away
                                        ref={popoverRef}
                                    >
                                        {field && (
                                            <FieldValueEditor
                                                uploadOptions={uploadOptions}
                                                videoUploadOptions={videoUploadOptions}
                                                richTextUploadOptions={richTextUploadOptions}
                                                dataSourceInfo={dataSource}
                                                data={currentData}
                                                currentRecord={currentRecord}
                                                scrollElement={scrollRef.current}
                                                isHiddenId
                                                width={`${width - 5}px`}
                                                field={field}
                                                recordId={recordId}
                                                autoFocus
                                                onCellChange={handleCellChange}
                                                onCellUpdate={onCellUpdate}
                                                onClose={handleClickOutside}
                                                onFetchPexelsList={onFetchPexelsList}
                                            />
                                        )}
                                    </SCxWindowShapeEditorPopover>
                                </ClickAwayListener>,
                                document.body
                            )}
                    </SCxDiv>
                )
            }

            return null
        }, [
            isMobile,
            isModalShape,
            isPopperShape,
            isInput,
            open,
            pageTarget,
            handleClickOutside,
            field,
            uploadOptions,
            videoUploadOptions,
            richTextUploadOptions,
            dataSource,
            currentData,
            currentRecord,
            scrollRef,
            recordId,
            handleCellChange,
            onCellUpdate,
            onFetchPexelsList,
            position,
            popperAnchorElement,
            mouseEvent,
            handleClose,
            width,
            popoverRef
        ])
    }
)
